// Bibliotecas
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ResponsiveScatterPlot, ScatterPlotValue } from '@nivo/scatterplot';

// Components
import ScatterChartCircle from '@/components/ScatterChartCircle';

// Types
import {
  ChartData,
  ChartStyles,
  HeaderBorderLineType,
  HeaderContainerType,
  IconContainerType,
  MinMax,
} from '@/types';
import { CartesianMarkerProps } from '@nivo/core';

// Utils
import { getColor } from '@/utils/colors';
import {
  WJP_BOTTOM_TICK_VALUES,
  WJP_DEFAULT_STYLES_SCATTER,
  WJP_SCATTER_GRID_X_VALUES,
} from '@/utils/const';

const ChartContainer = styled.div<IconContainerType>`
  width: ${({ width }) => width || 900}px;
  height: ${({ height }) => height || 155}px;
`;

const HeaderContainer = styled.div<HeaderContainerType>`
  background-color: ${({ backgroundColor }) => backgroundColor || '#fff'};
  color: ${({ fontColor }) => fontColor || '#000'};
  font-size: ${({ fontSize }) => fontSize || 12}px;
`;

const HeaderBorderLine = styled.div<HeaderBorderLineType>`
  border-color: ${({ borderColor }) => borderColor || '#cfcfcf'};
`;

type useWJPScatterChartsParams = {
  defaultStyles?: ChartStyles;
  chartData: ChartData[];
  minMax: MinMax[];
  chartMeans: (number | undefined)[];
};

const useWJPScatterCharts = ({
  defaultStyles = WJP_DEFAULT_STYLES_SCATTER,
  chartData,
  minMax,
  chartMeans,
}: useWJPScatterChartsParams) => {
  const [styles, setStyles] = useState<ChartStyles>(defaultStyles);

  const setMarkers = useCallback(
    (chartMeans: (number | undefined)[], chartMeanIndex: number) => {
      const markers: CartesianMarkerProps<ScatterPlotValue>[] = [];
      const mean = chartMeans[chartMeanIndex];

      if (chartMeans && chartMeans.length === chartData.length && mean) {
        markers.push({
          axis: 'x',
          value: mean,
          lineStyle: {
            stroke: styles.chartMeanStrokeColor,
            strokeWidth: styles.chartMeanStrokeWidth,
          },
        });
      }

      return markers.length > 0 ? markers : undefined;
    },
    [chartData.length, styles]
  );

  const charts = useMemo(
    () =>
      chartData.map((chart, i) => {
        const minMaxLabels = chartData[i].values
          .filter(
            (val) =>
              val.data[0].x === minMax[i]?.max ||
              val.data[0].x === minMax[i]?.min
          )
          .map((val) => ({
            label: val.id,
            value: val.data[0].x,
            type: val.data[0].x === minMax[i].max ? 'max' : 'min',
          }));

        const minArr = minMaxLabels.filter((data) => data.type === 'min');
        const maxArr = minMaxLabels.filter((data) => data.type === 'max');

        const minAnnotations = minArr.map((val, i) => {
          let noteX = -50;
          let noteY = -10;

          if (minArr.length >= 2) {
            noteY = -40 + i * 20;
            noteX = -60;
          }

          return {
            type: 'circle' as const,
            match: {
              index: 0,
            },
            noteX,
            noteY,
            note: typeof val.label === 'string' ? val.label : String(val.label),
            noteWidth: 0,
          };
        });

        const maxAnnotations = maxArr.map((val, i) => {
          const noteX = 10;
          let noteY = -10;

          if (maxArr.length >= 2) {
            noteY = -20 + i * 20;
          }

          return {
            type: 'circle' as const,
            match: {
              index: chart.values.length - 1,
            },
            noteX,
            noteY,
            note: typeof val.label === 'string' ? val.label : String(val.label),
            noteWidth: 0,
          };
        });

        const yScale = {
          min: styles.chartMinYValue,
          max: styles.chartMaxYValue,
          type: 'linear',
        } as const;

        const xScale = {
          min: styles.chartMinXValue,
          max: styles.chartMaxXValue,
          type: 'linear',
        } as const;

        return (
          <div className={i === 0 ? '' : 'ml-7'} key={chart.chart}>
            <div className="flex flex-row items-center">
              <HeaderContainer
                className="px-2 font-bold"
                backgroundColor={
                  i === 0 ? styles.headerBackground : styles.subHeaderBackground
                }
                fontColor={
                  i === 0 ? styles.headerFontColor : styles.subHeaderFontColor
                }
                fontSize={
                  i === 0 ? styles.headerFontSize : styles.subHeaderFontSize
                }
              >
                {i === 0 ? (
                  <h3 className="font-archer py-1 w-max">{chart.chart}</h3>
                ) : (
                  <h4 className="py-1 w-max">{chart.chart}</h4>
                )}
              </HeaderContainer>
              <HeaderBorderLine
                className="border ml-1 mr-[2px] w-full"
                borderColor={
                  i === 0 ? styles.headerBackground : styles.subHeaderBackground
                }
              />
            </div>
            <ChartContainer
              width={i === 0 ? styles.mainChartWidth : styles.subChartWidth}
              height={i === 0 ? styles.mainChartHeight : styles.subChartHeight}
              className={i === 0 ? 'ml-7' : ''}
            >
              <ResponsiveScatterPlot
                data={chart.values}
                yScale={yScale}
                xScale={xScale}
                margin={{
                  top: 10,
                  right: 10,
                  bottom: 35,
                  left: 10,
                }}
                theme={styles.theme}
                legends={undefined}
                blendMode="normal"
                nodeSize={14}
                xFormat=">-.2f"
                yFormat=">-.2f"
                axisTop={null}
                axisRight={null}
                enableGridY={false}
                axisLeft={null}
                tooltip={({ node }) => (
                  <div
                    style={{
                      color: getColor(
                        typeof node.xValue === 'number'
                          ? node.xValue
                          : Number(node.xValue)
                      ),
                      background: '#eee',
                      padding: '12px 16px',
                    }}
                  >
                    <strong>{node.serieId}</strong>
                    <br />
                    {node.formattedX}
                  </div>
                )}
                markers={setMarkers(chartMeans, i)}
                annotations={[...minAnnotations, ...maxAnnotations]}
                gridXValues={WJP_SCATTER_GRID_X_VALUES}
                axisBottom={{
                  tickSize: styles.tickSize,
                  tickPadding: styles.tickPadding,
                  tickRotation: 0,
                  tickValues: WJP_BOTTOM_TICK_VALUES,
                }}
                layers={[
                  'grid',
                  'axes',
                  'markers',
                  'nodes',
                  'mesh',
                  'legends',
                  'annotations',
                ]}
                colors={{ scheme: 'yellow_orange_red' }}
                nodeComponent={(props) => <ScatterChartCircle {...props} />}
              />
            </ChartContainer>
          </div>
        );
      }),
    [
      chartData,
      minMax,
      setMarkers,
      styles.chartMaxXValue,
      styles.chartMaxYValue,
      styles.chartMinXValue,
      styles.chartMinYValue,
      styles.headerBackground,
      styles.headerFontColor,
      styles.headerFontSize,
      styles.mainChartHeight,
      styles.mainChartWidth,
      styles.subChartHeight,
      styles.subChartWidth,
      styles.subHeaderBackground,
      styles.subHeaderFontColor,
      styles.subHeaderFontSize,
      styles.theme,
      styles.tickPadding,
      styles.tickSize,
    ]
  );

  return { charts, styles, setStyles };
};

export default useWJPScatterCharts;
